import { useState } from 'react'
import { SYSTEM_MESSAGE_IDS } from '../../../../shared/constants/systemMessages.constants'
import { useTranslation } from 'react-i18next'
import {
  getClientList,
  getFileAccountTemplate,
  postFileAccountUpload,
} from '../../../../shared/services/AccountService'

export enum EnumSelectAction {
  create,
  edit,
}

export interface IFormUpload {
  clientId: string | null
  selectAction: EnumSelectAction
  uploadAccountFile: any
}

export const fileExtension = ['.xlsx']

const FILE_ACCEPT: {
  [key: string]: string
} = {
  excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  excel_xls: 'application/vnd.ms-excel',
}
export const LIMITED_FILE_SIZE = 10485760

interface IResErrorMessage {
  rowName: string
  status: string
  messages: string[]
}

export interface IResImportFile {
  importType: number
  importTypeName: string
  fileName: string
  inputDate: string
  issuedDate: string
  status: number
  statusName: string
  processedCount: number
  processedItems: any[]
  rejectedCount: number
  rejectedItems: any[]
  uploadedBy: string
  uploadPath: string
  uploadedPath: string
  rowErrors: IResErrorMessage[]
}

export const useClientSelectData = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [clientOptions, setClientOptions] = useState<Array<{ label: string; value: string }>>([])
  const [totalRecords, setTotalRecords] = useState(0)

  const formatResData = (data: any): { label: string; value: string }[] => {
    return data?.map((item: any) => ({
      label: item.clientName,
      value: item.id,
    }))
  }

  const getClientOptions = async () => {
    try {
      setIsLoading(true)

      const data = await getClientList()
      setClientOptions(formatResData(data?.data))
      setTotalRecords(data?.data?.total)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { clientOptions, totalRecords, getClientOptions, isLoading }
}

export const useFormRules = () => {
  const { t } = useTranslation()

  const messageClientNameRequired = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
    Required_Field: `${t('AccountSetup:uploadFile.clientName')}`,
  })
  const messageAccountFileRequired = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
    Required_Field: `${t('AccountSetup:uploadFile.uploadAccountFile')}`,
  })

  return {
    clientName: {
      required: {
        value: true,
        message: messageClientNameRequired,
      },
    },
    uploadAccountFile: {
      required: {
        value: true,
        message: messageAccountFileRequired,
      },
    },
  }
}

export const validateFile = (file: any, t: any): string => {
  /*VALIDATE FILE EXTENSION*/
  const regex = /\.xlsx\b/

  if (!Object.values(FILE_ACCEPT).includes(file.type) || !regex.test(file.name)) {
    return `${t('AccountSetup:uploadFile.PPP02')}`
  }

  /*VALIDATE FILE SIZE*/
  if (file.size > LIMITED_FILE_SIZE) {
    return `${t('AccountSetup:uploadFile.PPP01')}`
  }

  return ''
}

export const useDownloadFileTemplate = () => {
  const [isLoading, setIsLoading] = useState(false)

  const downloadFileAccountTemplate = async (clientId: string, isEdit: boolean) => {
    try {
      setIsLoading(true)

      const data = await getFileAccountTemplate(clientId, isEdit)
      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { downloadFileAccountTemplate, isLoading }
}

export const useImportFileTemplate = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [resImportFileTemplate, setResImportFileTemplate] = useState<IResImportFile>()

  const importFileAccountTemplate = async (data: IFormUpload) => {
    try {
      setIsLoading(true)

      const { clientId, selectAction } = data

      const formData: any = new FormData()

      formData.append('Accounts', data.uploadAccountFile.originFileObj)
      formData.append('ClientId', clientId)
      formData.append('UploadType', selectAction === EnumSelectAction.edit ? 1 : 0)
      const res = await postFileAccountUpload(formData)
      setResImportFileTemplate(res?.data?.dataImportProcessing)
      return res?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { importFileAccountTemplate, isLoading, resImportFileTemplate }
}

export const getErrorMessage = (error: IResErrorMessage, t: any): string => {
  let result = error?.rowName
    ? `Row ${error.rowName}: ${error?.messages.join(', ')} `
    : ` ${error?.messages.join(', ')} `

  return result
}

export const checkFnRunThan30s = (fn: any): boolean => {
  let result: boolean = false
  const promise30s = new Promise((resolve, reject) => {
    setTimeout(resolve, 30000, '30s')
  })

  const fnUpload = new Promise((resolve, reject) => {
    return fn()
  })

  Promise.race([promise30s, fnUpload]).then((value) => {
    if (value === '30s') {
      result = true
    }
  })

  return result
}
